// ============================================================================
// Banderas globales en el front (épica #1219) — SÓLO presentación.
// ============================================================================
// Lee `GET /public/entitlements/flags` (sin sesión): las llaves del catálogo de
// WAYAK que hoy NO están encendidas para todos (`off` o `internal`; la ausencia
// de una llave = encendida). Con eso se ocultan mosaicos y se cierran rutas.
// La autoridad sigue siendo el backend.
//
// Regla de oro: si todavía no hay respuesta (primera carga, API caída) NO se
// oculta nada. Las banderas son las mismas para todos los usuarios, así que no
// se atan a una sesión.
//
// WAYAK sólo tiene el nivel 1 (banderas). Cuando llegue el port completo de
// licencias (#660), esto se reemplaza por el `entitlementsStore` de CC-CONSOLE.
// ============================================================================
import { create } from "zustand";
import { persist } from "zustand/middleware";
import api from "../utils/api";

export type FlagState = "on" | "off" | "internal";

export interface PublicFlags {
  catalogVersion: number;
  /** Llave → estado, sólo de lo que NO está encendido para todos. */
  restricted: Record<string, Exclude<FlagState, "on">>;
}

interface FeatureFlagsRuntimeState {
  data: PublicFlags | null;
  fetchedAt: number | null;
  loading: boolean;
  /** Pide las banderas. Sin `force`, respeta el TTL. */
  refresh: (opts?: { force?: boolean }) => Promise<void>;
  reset: () => void;
}

/** Cuánto se reutiliza la respuesta antes de volver a preguntar. */
export const FLAGS_TTL_MS = 60 * 1000;

const isRestricted = (v: unknown): v is Exclude<FlagState, "on"> => v === "off" || v === "internal";

/** Valida la forma de la respuesta: una respuesta rara no debe ocultar nada. */
export function parsePublicFlags(body: unknown): PublicFlags | null {
  const payload = (body as { data?: unknown } | null)?.data as
    | { catalogVersion?: unknown; restricted?: unknown }
    | undefined;
  if (!payload || typeof payload !== "object") return null;
  const restricted: PublicFlags["restricted"] = {};
  const raw = payload.restricted;
  if (!raw || typeof raw !== "object") return null;
  for (const [key, state] of Object.entries(raw as Record<string, unknown>)) {
    if (isRestricted(state)) restricted[key] = state;
  }
  return { catalogVersion: Number(payload.catalogVersion) || 0, restricted };
}

let latestRequest = 0;

export const useFeatureFlagsRuntimeStore = create<FeatureFlagsRuntimeState>()(
  persist(
    (set, get) => ({
      data: null,
      fetchedAt: null,
      loading: false,
      refresh: async ({ force = false } = {}) => {
        if (get().loading && !force) return;
        const { data, fetchedAt } = get();
        if (!force && data && fetchedAt && Date.now() - fetchedAt < FLAGS_TTL_MS) return;

        const request = ++latestRequest;
        const startedAt = Date.now();
        set({ loading: true });
        try {
          const res = await api.get("/public/entitlements/flags");
          if (request !== latestRequest) return;
          const next = parsePublicFlags(res?.data);
          // Una respuesta ilegible no pisa lo último conocido.
          set(next ? { data: next, fetchedAt: startedAt, loading: false } : { loading: false });
        } catch {
          // Se conserva lo último conocido; si no hay nada, no se oculta nada.
          if (request === latestRequest) set({ loading: false });
        }
      },
      reset: () => {
        latestRequest += 1;
        set({ data: null, fetchedAt: null, loading: false });
      },
    }),
    {
      name: "featureFlagsRuntime",
      partialize: (s) => ({ data: s.data, fetchedAt: s.fetchedAt }),
    },
  ),
);

// ---------------------------------------------------------------------------
// Revalidación en segundo plano: al arrancar, al volver a la pestaña y cada
// 5 minutos (un cambio llega a todos en ≤ 5 min).
// ---------------------------------------------------------------------------
const REVALIDATE_MS = 5 * 60 * 1000;
let started = false;

export function startFeatureFlagsRevalidation() {
  if (started || typeof window === "undefined") return;
  started = true;
  const refresh = () => void useFeatureFlagsRuntimeStore.getState().refresh();
  refresh();
  window.setInterval(() => void useFeatureFlagsRuntimeStore.getState().refresh({ force: true }), REVALIDATE_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") refresh();
  });
  window.addEventListener("online", refresh);
}
